import { createSelector } from 'reselect';

import {
  selectEntitiesMissionsEntity,
  selectEntitiesMissionsEntityDescription,
  selectEntitiesMissionsEntityEndDate,
  selectEntitiesMissionsEntityImageUrl,
  selectEntitiesMissionsEntityLogoUrl,
  selectEntitiesMissionsEntityOperationType,
  selectEntitiesMissionsEntityOptInEnabled,
  selectEntitiesMissionsEntityShowTimer,
} from '../../../../selectors/entities/missions/entitiesMissionsPrimary.selector';
import {
  selectUserConnected,
  selectUserMissionsModerator,
  selectUserPromotionStatus,
} from '../../../../selectors/user/userPrimary.selector';
import { BetslipStatuses } from '../../../../types/betslip';
import { PromotionStatus } from '../../../../types/promotions';
import { Selector } from '../../../../types/selectors';
import { selectMissionAchievements, selectMissionAchievementsRewards } from '../../selectors/missionAchievements';
import { selectUserMissionAchievements } from '../../selectors/userMissionAchievements';
import {
  selectUserMission,
  selectUserMissionHasUserRegistered,
  selectUserMissionStatus,
} from '../../selectors/userMissions';
import { MissionOperationType } from '../../types/state/missions';
import { UserAchievementStatuses, UserMissionStatus, UserMissionStatuses } from '../../types/state/userMissions';
import { selectMissionBetRedirectionCallback } from '../MissionDetailsBetRedirectionButton/MissionDetailsBetRedirectionButton.selector';
import { selectMissionFreebetsPotentialGain } from '../MissionFreebetsPotentialGain/MissionFreebetsPotentialGain.selector';

const selectMissionAchievementsMinBetOdds: Selector<number | undefined> = createSelector(
  [selectMissionAchievements],
  (achievements) => {
    if (achievements.length === 1) {
      return achievements[0].eligibility.minBetOdds;
    }

    const uniqueAchievementsMinBetOdds = [
      ...new Set(achievements.map((achievement) => achievement.eligibility.minBetOdds).filter((k) => k)),
    ];

    return uniqueAchievementsMinBetOdds.length === 1 ? uniqueAchievementsMinBetOdds[0] : undefined;
  },
);

const selectMissionAchievementsMinPlacedAmount: Selector<number | undefined> = createSelector(
  [selectMissionAchievements],
  (achievements) => {
    if (achievements.length === 1 && achievements[0].eligibility?.minPlacedAmount) {
      return achievements[0].eligibility.minPlacedAmount * 100;
    }

    const uniqueAchievementsMinPlacedAmount = [
      ...new Set(achievements.map((achievement) => achievement.eligibility.minPlacedAmount).filter((k) => k)),
    ];

    return uniqueAchievementsMinPlacedAmount[0] ? uniqueAchievementsMinPlacedAmount[0] * 100 : undefined;
  },
);

const selectAchievementsNbPendingBets: Selector<number> = createSelector(
  [selectUserMissionAchievements],
  (userMissionAchievements) =>
    userMissionAchievements
      .filter(
        (userMissionAchievement) =>
          userMissionAchievement?.status !== UserAchievementStatuses.WON && !userMissionAchievement?.wonDate,
      )
      .reduce((result, userMissionAchievement) => {
        const betslipIds = Object.keys(userMissionAchievement?.betslips ?? {});

        if (betslipIds.length) {
          const betslips = betslipIds
            .map((betslipId) => userMissionAchievement?.betslips?.[betslipId])
            .filter((k) => k);
          const nbPendingBetslips = betslips.filter(
            (betslip) => betslip?.betslipStatus === BetslipStatuses.RUNNING,
          ).length;

          return result + nbPendingBetslips;
        }

        return result;
      }, 0),
);

export const selectUserMissionWonAmount: Selector<number | undefined> = createSelector(
  [selectUserMission, selectEntitiesMissionsEntity, selectMissionAchievementsRewards],
  (userMission, mission, missionAchievementRewards) => {
    if (userMission?.status === UserMissionStatuses.WON) {
      const missionWonAmount = mission?.reward?.rewardParams?.amount ?? 0;
      const achievementsWonAmount = missionAchievementRewards.reduce(
        (result, reward) => result + (reward?.rewardParams?.amount ?? 0),
        0,
      );

      return missionWonAmount + achievementsWonAmount;
    }

    return undefined;
  },
);

type MissionComplexeStatusComputationPropsType = {
  endDate?: number;
  status?: UserMissionStatus;
  nbAchievementsPendingBets: number;
  hasUserRegistered: boolean;
  optInRequired: boolean;
};

export const selectMissionComplexeStatusComputationProps: Selector<MissionComplexeStatusComputationPropsType> =
  createSelector(
    [
      selectEntitiesMissionsEntityEndDate,
      selectUserMissionStatus,
      selectAchievementsNbPendingBets,
      selectUserMissionHasUserRegistered,
      selectEntitiesMissionsEntityOptInEnabled,
    ],
    (endDate, status, nbAchievementsPendingBets, hasUserRegistered, optInRequired) => ({
      endDate,
      status,
      nbAchievementsPendingBets,
      hasUserRegistered,
      optInRequired,
    }),
  );

export type MissionCardContainerPropsType = {
  description?: string;
  logoURL?: string | null;
  backgroundURL?: string | null;
  achvMinBetOdds?: number;
  achvMinPlacedAmount?: number;
  operationType?: MissionOperationType;
  wonAmount?: number;
  showTimer: boolean;
  hasUserRegistered: boolean;
  optInRequired: boolean;
  isUserConnected: boolean;
  betRedirectionOnClick: () => void;
  promotionStatus: PromotionStatus;
  missionsModerator: boolean;
  hasPotentialAmount: boolean;
};

const selectMissionCardContainerProps: Selector<MissionCardContainerPropsType> = createSelector(
  [
    selectEntitiesMissionsEntityDescription,
    selectEntitiesMissionsEntityLogoUrl,
    selectEntitiesMissionsEntityImageUrl,
    selectMissionAchievementsMinBetOdds,
    selectMissionAchievementsMinPlacedAmount,
    selectEntitiesMissionsEntityOperationType,
    selectUserMissionWonAmount,
    selectEntitiesMissionsEntityShowTimer,
    selectUserMissionHasUserRegistered,
    selectEntitiesMissionsEntityOptInEnabled,
    selectUserConnected,
    selectMissionBetRedirectionCallback,
    selectUserPromotionStatus,
    selectUserMissionsModerator,
    selectMissionFreebetsPotentialGain,
  ],
  (
    description,
    logoURL,
    backgroundURL,
    achvMinBetOdds,
    achvMinPlacedAmount,
    operationType,
    wonAmount,
    showTimer,
    hasUserRegistered,
    optInRequired,
    isUserConnected,
    betRedirectonCallback,
    promotionStatus,
    missionsModerator,
    potentialAmount,
  ) => ({
    description,
    logoURL,
    backgroundURL,
    achvMinBetOdds,
    achvMinPlacedAmount,
    operationType,
    wonAmount,
    showTimer,
    hasUserRegistered,
    optInRequired,
    isUserConnected,
    betRedirectionOnClick: betRedirectonCallback ?? (() => false),
    promotionStatus,
    missionsModerator,
    hasPotentialAmount: !!potentialAmount,
  }),
);

export default selectMissionCardContainerProps;
